import React, { useEffect, useState, useCallback } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useAuthHeaders from '../../hooks/useAuthHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';
const PAGE_SIZE = 50;

const MONTH_NAMES = [
  '', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

const currentYearMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const parseYearMonth = (str) => {
  if (!str) return { year: null, month: null };
  const [y, m] = str.split('-').map(Number);
  return { year: y || null, month: m || null };
};

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
  return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
};

const COLUMNS = [
  { key: 'email',            label: 'Email' },
  { key: 'period',           label: 'Periodo' },
  { key: 'distinctrfccount', label: 'RFCs únicos' },
  { key: 'totalrequests',    label: 'Total solicitudes' },
];

const AdminMonthlyRfcReport = () => {
  const { getAuthHeaders } = useAuthHeaders();
  const navigate = useNavigate();

  const thisMonth = currentYearMonth();

  // Inputs
  const [emailInput, setEmailInput]         = useState('');
  const [fromMonthInput, setFromMonthInput] = useState(thisMonth);
  const [toMonthInput, setToMonthInput]     = useState(thisMonth);

  // Active query
  const [query, setQuery] = useState(() => {
    const { year, month } = parseYearMonth(thisMonth);
    return {
      email: '', yearFrom: year, monthFrom: month, yearTo: year, monthTo: month,
      sortBy: 'period', sortDir: 'desc', page: 1, pageSize: PAGE_SIZE,
    };
  });

  // Results
  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (q.email)      params.set('email', q.email);
      if (q.yearFrom)   params.set('yearFrom',  q.yearFrom);
      if (q.monthFrom)  params.set('monthFrom', q.monthFrom);
      if (q.yearTo)     params.set('yearTo',  q.yearTo);
      if (q.monthTo)    params.set('monthTo', q.monthTo);
      params.set('sortBy',   q.sortBy);
      params.set('sortDir',  q.sortDir);
      params.set('page',     q.page);
      params.set('pageSize', q.pageSize);

      const response = await fetch(`${API_BASE}/admin/reports/monthly-rfc-usage?${params}`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData(query);
  }, [query, fetchData]);

  const handleSearch = () => {
    const from = parseYearMonth(fromMonthInput);
    const to   = parseYearMonth(toMonthInput);
    setQuery(prev => ({
      ...prev,
      email:      emailInput,
      yearFrom:   from.year,
      monthFrom:  from.month,
      yearTo:     to.year,
      monthTo:    to.month,
      page: 1,
    }));
  };

  const handleClear = () => {
    const { year, month } = parseYearMonth(thisMonth);
    setEmailInput('');
    setFromMonthInput(thisMonth);
    setToMonthInput(thisMonth);
    setQuery(prev => ({
      ...prev,
      email: '', yearFrom: year, monthFrom: month, yearTo: year, monthTo: month,
      page: 1,
    }));
  };

  const handleSort = (col) => {
    setQuery(prev => ({
      ...prev,
      sortBy:  col,
      sortDir: prev.sortBy === col && prev.sortDir === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  };

  const handlePage = (p) => setQuery(prev => ({ ...prev, page: p }));

  const totalPages  = result.totalPages ?? 0;
  const currentPage = result.page ?? 1;

  const buildPageItems = () => {
    const items = [];
    const delta = 2;
    const left  = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);
    if (left > 1) { items.push(1); if (left > 2) items.push('...'); }
    for (let i = left; i <= right; i++) items.push(i);
    if (right < totalPages) { if (right < totalPages - 1) items.push('...'); items.push(totalPages); }
    return items;
  };

  return (
    <div className="status-container">
      <div className="page-header mb-4">
        <div>
          <button
            className="btn btn-link p-0 mb-1 text-muted small"
            onClick={() => navigate('/admin/reports')}
          >
            ← Reportes
          </button>
          <h2 className="page-title">RFCs únicos por mes</h2>
          <p className="page-subtitle text-muted">
            RFCs distintos utilizados por usuario según actividad registrada en DailySummaries.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 p-3 border rounded bg-light">
        <Row className="g-2 align-items-end">
          <Col xs={12} md={4}>
            <Form.Label className="small mb-1">Email</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Filtrar por email..."
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={6} md={3}>
            <Form.Label className="small mb-1">Desde (mes)</Form.Label>
            <Form.Control
              size="sm"
              type="month"
              value={fromMonthInput}
              onChange={e => setFromMonthInput(e.target.value)}
            />
          </Col>
          <Col xs={6} md={3}>
            <Form.Label className="small mb-1">Hasta (mes)</Form.Label>
            <Form.Control
              size="sm"
              type="month"
              value={toMonthInput}
              onChange={e => setToMonthInput(e.target.value)}
            />
          </Col>
          <Col xs={12} md={2} className="d-flex gap-1">
            <Button size="sm" variant="primary" onClick={handleSearch} className="w-100">
              Buscar
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={handleClear} title="Limpiar">
              ✕
            </Button>
          </Col>
        </Row>
      </div>

      {error && <Alert variant="danger">Error al cargar datos: {error}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" style={{ color: '#13a0ff' }} />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-2">
              <thead className="table-light">
                <tr>
                  {COLUMNS.map(({ key, label }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
                    >
                      {label}
                      <SortIcon col={key} sortBy={query.sortBy} sortDir={query.sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.items.map((row, i) => (
                  <tr key={i}>
                    <td className="small">{row.email ?? '—'}</td>
                    <td className="small" style={{ whiteSpace: 'nowrap' }}>
                      {MONTH_NAMES[row.month]} {row.year}
                    </td>
                    <td className="small fw-semibold">{row.distinctRfcCount}</td>
                    <td className="small">{row.totalRequestCount.toLocaleString()}</td>
                  </tr>
                ))}
                {result.items.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      No se encontraron datos con los filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              {result.totalCount} resultado{result.totalCount !== 1 ? 's' : ''}
              {totalPages > 1 && ` — Página ${currentPage} de ${totalPages}`}
            </small>
            {totalPages > 1 && (
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePage(currentPage - 1)} />
                {buildPageItems().map((item, i) =>
                  item === '...'
                    ? <Pagination.Ellipsis key={`e${i}`} disabled />
                    : <Pagination.Item key={item} active={item === currentPage} onClick={() => handlePage(item)}>{item}</Pagination.Item>
                )}
                <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePage(currentPage + 1)} />
              </Pagination>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminMonthlyRfcReport;
